import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Languages, MapPin, Monitor, Plus, Search, Smartphone } from "lucide-react";

import { Button } from "@/components/Button";
import { CountryFlag } from "@/components/CountryFlag";
import { CustomSelector } from "@/components/CustomSelector";
import { brand } from "@/theme/theme";
import {
  AI_ENGINES,
  CATALOG_STALE_TIME,
  CLASSIC_ENGINE_FAMILIES,
  getRankTrackingCountries,
  getRankTrackingEngines,
  getRankTrackingLanguages,
  getRankTrackingLocations,
  rankTrackingKeys,
} from "@/services/rankTrackingService";
import {
  DEVICE_LABELS,
  FAMILY_LABELS,
  buildEngineKey,
  engineTitle,
  fieldSx,
  isAiEngine,
  isLocatedEngine,
  sameEngine,
} from "../seoSetupUtils";
import { useDebounced, useFlashNotice } from "../hooks";
import {
  BORDER,
  EmptyState,
  EngineIcon,
  EngineTable,
  FieldLabel,
  RemoveButton,
  SUCCESS,
  ServerErrors,
  SetupCard,
} from "../components";

const EMPTY = [];

// Interface language suggested for a country; falls back to the country code.
const COUNTRY_LANGUAGE = {
  ES: "es", MX: "es", AR: "es", CO: "es", CL: "es", PE: "es", VE: "es", EC: "es", UY: "es", PY: "es",
  BO: "es", CR: "es", PA: "es", DO: "es", GT: "es", HN: "es", SV: "es", NI: "es", PR: "es",
  US: "en", GB: "en", IE: "en", AU: "en", NZ: "en", CA: "en", IN: "en",
  FR: "fr", BE: "fr", CH: "de", DE: "de", AT: "de", IT: "it", PT: "pt", BR: "pt", NL: "nl", AD: "ca",
};

const defaultLanguage = (country, languages) => {
  if (!languages.length) return "";
  const codes = languages.map((language) => String(language.code));
  const find = (code) =>
    codes.find((c) => c.toLowerCase() === code) || codes.find((c) => c.toLowerCase().startsWith(`${code}-`));
  const wanted = COUNTRY_LANGUAGE[String(country || "").toUpperCase()] || String(country || "").toLowerCase();
  return find(wanted) || find("es") || find("en") || codes[0];
};

const catalogEntry = (catalog, family, device) => {
  const ofFamily = catalog.filter((engine) => engine.family === family);
  const byDevice = (wanted) => ofFamily.find((engine) => (engine.device || "desktop") === wanted);
  // Only Google offers a device choice; other families take their desktop (or only) entry.
  return family === "google" ? byDevice(device) || null : byDevice("desktop") || ofFamily[0] || null;
};

const barSx = {
  display: "inline-flex",
  border: `1px solid ${BORDER}`,
  borderRadius: "10px",
  overflow: "hidden",
  backgroundColor: "#fff",
  "& > button + button": { borderLeft: `1px solid ${BORDER}` },
};

const barButtonSx = (selected, disabled) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  px: 1.75,
  minHeight: 42,
  border: "none",
  font: "inherit",
  fontSize: "0.9375rem",
  fontWeight: selected ? 700 : 500,
  whiteSpace: "nowrap",
  color: disabled ? "text.disabled" : brand.ink,
  backgroundColor: selected ? "#F1F5F9" : "transparent",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  transition: "background-color 0.15s",
  "&:hover": disabled || selected ? {} : { backgroundColor: "#F8FAFC" },
  "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: -2 },
});

// Classic search engines and AI engines in separate bars, every option in sight.
const CLASSIC_TABS = ["google", "bing", "yahoo"];
const AI_TABS = ["google_ai_overview", "google_ai_mode", "chatgpt", "gemini", "perplexity"];
const AI_ENGINE_IDS = AI_ENGINES.map((engine) => engine.id);

/** One labelled bar of search engines, like the tabs of the provider's own setup. */
function EngineTabs({ label, families, value, onChange, available, isLoading, children }) {
  return (
    <Box sx={{ maxWidth: "100%" }}>
      <Typography variant="caption" sx={{ display: "block", mb: 0.75, fontWeight: 600, color: "text.secondary" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        {isLoading ? (
          <Skeleton variant="rounded" width={families.length * 130} height={44} sx={{ maxWidth: "100%" }} />
        ) : (
          <Box sx={{ maxWidth: "100%", overflowX: "auto" }}>
            <Box role="radiogroup" aria-label={label} sx={barSx}>
              {families.map((family) => {
                const selected = family === value;
                const disabled = !available.includes(family);
                return (
                  <Box
                    key={family}
                    component="button"
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={disabled}
                    onClick={() => onChange(family)}
                    sx={barButtonSx(selected, disabled)}
                  >
                    <EngineIcon family={family} size={22} bare />
                    {FAMILY_LABELS[family]}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
        {children}
      </Box>
    </Box>
  );
}

EngineTabs.propTypes = {
  label: PropTypes.string.isRequired,
  families: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  available: PropTypes.arrayOf(PropTypes.string).isRequired,
  isLoading: PropTypes.bool,
  children: PropTypes.node,
};

function DeviceToggle({ value, onChange, mobileAvailable }) {
  const options = [
    { value: "desktop", Icon: Monitor, disabled: false },
    { value: "mobile", Icon: Smartphone, disabled: !mobileAvailable },
  ];
  return (
    <Box role="radiogroup" aria-label="Dispositivo" sx={barSx}>
      {options.map(({ value: device, Icon, disabled }) => (
        <Tooltip key={device} title={DEVICE_LABELS[device]}>
          <Box
            component="button"
            type="button"
            role="radio"
            aria-checked={value === device}
            aria-label={DEVICE_LABELS[device]}
            disabled={disabled}
            onClick={() => onChange(device)}
            sx={{ ...barButtonSx(value === device, disabled), px: 1.25 }}
          >
            <Icon size={18} />
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

DeviceToggle.propTypes = { value: PropTypes.string, onChange: PropTypes.func.isRequired, mobileAvailable: PropTypes.bool };

/** Dropdown body of the location field: a search box over the provider's Google locations. */
function LocationMenu({ country, countryName, onSelect }) {
  const [input, setInput] = useState("");
  const query = useDebounced(input.trim(), 300);
  const locationsQuery = useQuery({
    queryKey: rankTrackingKeys.locations(country, query),
    queryFn: () => getRankTrackingLocations(country, query),
    staleTime: CATALOG_STALE_TIME,
    enabled: Boolean(country) && Boolean(query),
  });
  const results = locationsQuery.data || EMPTY;

  const item = (key, label, onClick, muted = false) => (
    <Box
      key={key}
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        width: "100%",
        display: "block",
        px: 1.5,
        py: 1,
        border: "none",
        background: "transparent",
        font: "inherit",
        fontSize: "0.875rem",
        textAlign: "left",
        color: muted ? "text.secondary" : "text.primary",
        cursor: "pointer",
        "&:hover, &:focus-visible": { backgroundColor: "#F1F5F9", outline: "none" },
      }}
    >
      {label}
    </Box>
  );

  return (
    <Box>
      <Box sx={{ p: 1, borderBottom: `1px solid ${BORDER}` }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="Buscar ubicación"
          aria-label="Buscar ubicación"
          sx={fieldSx}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
              endAdornment: locationsQuery.isFetching ? <CircularProgress size={14} /> : null,
            },
          }}
        />
      </Box>
      <Box sx={{ py: 0.5 }}>
        {item("all", `Todo ${countryName || "el país"}`, () => onSelect(null), true)}
        {!query ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
            Escribe el nombre de una ciudad o región.
          </Typography>
        ) : !locationsQuery.isFetching && !results.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
            Sin resultados para «{query}».
          </Typography>
        ) : (
          results.map((location) => item(location.region_name, location.label, () => onSelect(location)))
        )}
      </Box>
    </Box>
  );
}

LocationMenu.propTypes = { country: PropTypes.string, countryName: PropTypes.string, onSelect: PropTypes.func.isRequired };

function EngineForm({ engines, onAdd, defaultCountry, brandName, onBrandChange, brandError }) {
  const [family, setFamily] = useState("google");
  const [device, setDevice] = useState("desktop");
  const [country, setCountry] = useState(defaultCountry || "ES");
  const [location, setLocation] = useState(null);
  const [langCode, setLangCode] = useState("");
  const [notice, setNotice] = useFlashNotice(2500);
  const isGoogle = family === "google";
  const isAi = AI_ENGINE_IDS.includes(family);
  const located = isLocatedEngine(family);
  const hasLanguage = isGoogle || isAi;

  const countriesQuery = useQuery({
    queryKey: rankTrackingKeys.countries(),
    queryFn: getRankTrackingCountries,
    staleTime: CATALOG_STALE_TIME,
  });
  const enginesQuery = useQuery({
    queryKey: rankTrackingKeys.engines(country),
    queryFn: () => getRankTrackingEngines(country),
    staleTime: CATALOG_STALE_TIME,
    enabled: Boolean(country),
  });
  const languagesQuery = useQuery({
    queryKey: rankTrackingKeys.languages(),
    queryFn: getRankTrackingLanguages,
    staleTime: CATALOG_STALE_TIME,
  });

  const countries = countriesQuery.data || EMPTY;
  const catalog = enginesQuery.data || EMPTY;
  const languages = languagesQuery.data || EMPTY;

  // A country missing from the catalogue (e.g. a stale default) falls back to Spain or the first one.
  useEffect(() => {
    if (countries.length && !countries.some((c) => c.code === country)) {
      setCountry(countries.find((c) => c.code === "ES")?.code || countries[0].code);
    }
  }, [countries, country]);

  useEffect(() => {
    setLangCode(defaultLanguage(country, languages));
    setLocation(null);
  }, [country, languages]);

  const availableFamilies = useMemo(
    () => [...CLASSIC_ENGINE_FAMILIES.filter((f) => catalog.some((engine) => engine.family === f)), ...AI_ENGINE_IDS],
    [catalog]
  );

  useEffect(() => {
    if (availableFamilies.length && !availableFamilies.includes(family)) setFamily(availableFamilies[0]);
  }, [availableFamilies, family]);

  const countryOptions = useMemo(
    () => countries.map((c) => ({ value: c.code, label: c.name, icon: <CountryFlag code={c.code} /> })),
    [countries]
  );
  const languageOptions = useMemo(
    () => languages.map((l) => ({ value: String(l.code), label: l.name })),
    [languages]
  );
  const locationOptions = useMemo(
    () => (location ? [{ value: location.region_name, label: location.label }] : EMPTY),
    [location]
  );

  const entry = isAi ? null : catalogEntry(catalog, family, device);
  const countryName = countries.find((c) => c.code === country)?.name || country;

  const regionName = located ? location?.region_name || "" : "";
  const candidate = isAi
    ? { family, country, region_name: regionName, lang_code: langCode }
    : entry && {
      family,
      device: isGoogle ? device : entry.device || "desktop",
      search_engine_id: entry.search_engine_id,
      country,
      region_name: regionName,
      lang_code: isGoogle ? langCode : "",
    };
  const duplicate = candidate && engines.some((engine) => sameEngine(engine, candidate));
  const canAdd =
    Boolean(candidate) && !duplicate && (!isGoogle || Boolean(langCode)) && (!isAi || Boolean(brandName.trim()));

  const handleAdd = () => {
    if (!canAdd) return;
    onAdd({
      ...candidate,
      key: buildEngineKey(candidate),
      display: {
        engine: entry?.name || engineTitle(candidate),
        country: countryName,
        location: located ? location?.label || "" : "",
        language: hasLanguage ? languages.find((l) => String(l.code) === String(langCode))?.name || langCode : "",
      },
    });
    setNotice(`${[engineTitle(candidate), located ? location?.label : ""].filter(Boolean).join(" · ")} añadido.`);
    setLocation(null);
  };

  return (
    <SetupCard
      title="Configurar motores de búsqueda"
      description="Elige en qué buscadores tradicionales y de IA, dispositivos y países quieres seguir al cliente. En Google, AI Overviews, AI Mode y Gemini puedes afinar hasta la ciudad."
      footer={
        <>
          {isAi && !brandName.trim() ? (
            <Typography variant="caption" color="text.secondary">
              Indica el nombre de la marca para añadir motores de IA.
            </Typography>
          ) : notice ? (
            // Before the duplicate hint: the form resets after adding and may land on an engine already listed.
            <Typography variant="caption" sx={{ color: SUCCESS, fontWeight: 600 }} role="status">
              {notice}
            </Typography>
          ) : duplicate ? (
            <Typography variant="caption" color="text.secondary">
              Este motor ya está en la lista.
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Puedes añadir tantos motores como necesites.
            </Typography>
          )}
          <Button variant="dark" onClick={handleAdd} disabled={!canAdd} sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Plus size={16} />
            Añadir motor de búsqueda
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        <Box sx={{ display: "flex", columnGap: 3, rowGap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          <EngineTabs
            label="Buscadores"
            families={CLASSIC_TABS}
            value={family}
            onChange={setFamily}
            available={availableFamilies}
            isLoading={enginesQuery.isLoading}
          >
            {isGoogle && (
              <DeviceToggle
                value={device}
                onChange={setDevice}
                mobileAvailable={enginesQuery.isLoading || Boolean(catalogEntry(catalog, "google", "mobile"))}
              />
            )}
          </EngineTabs>
          <EngineTabs
            label="Motores de IA"
            families={AI_TABS}
            value={family}
            onChange={setFamily}
            available={availableFamilies}
            isLoading={enginesQuery.isLoading}
          />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 2, rowGap: 2 }}>
          <Box>
            <FieldLabel>País</FieldLabel>
            <CustomSelector
              searchable
              value={country}
              onChange={setCountry}
              options={countryOptions}
              leadingIcon={<CountryFlag code={country} />}
              disabled={countriesQuery.isLoading}
              placeholder={countriesQuery.isLoading ? "Cargando países..." : "Selecciona un país"}
            />
          </Box>

          {located && (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <FieldLabel>Ubicación</FieldLabel>
                <Typography variant="caption" color="text.secondary">
                  Opcional
                </Typography>
              </Box>
              <CustomSelector
                value={location?.region_name || ""}
                options={locationOptions}
                onChange={() => {}}
                placeholder={`Todo ${countryName || "el país"}`}
                leadingIcon={<MapPin size={16} color="#64748B" />}
                menuMaxHeight={340}
                renderMenuContent={({ closeDropdown }) => (
                  <LocationMenu
                    key={country}
                    country={country}
                    countryName={countryName}
                    onSelect={(next) => {
                      setLocation(next);
                      closeDropdown();
                    }}
                  />
                )}
              />
            </Box>
          )}

          {hasLanguage && (
            <Box>
              <FieldLabel>{isGoogle ? "Idioma de la interfaz de Google" : "Idioma"}</FieldLabel>
              <CustomSelector
                searchable
                value={langCode}
                onChange={setLangCode}
                options={languageOptions}
                disabled={languagesQuery.isLoading}
                placeholder={languagesQuery.isLoading ? "Cargando idiomas..." : "Selecciona un idioma"}
                leadingIcon={<Languages size={16} color="#64748B" />}
              />
            </Box>
          )}

          {isAi && (
            <Box>
              <FieldLabel htmlFor="rank-tracking-brand">Nombre de la marca</FieldLabel>
              <TextField
                id="rank-tracking-brand"
                fullWidth
                size="small"
                value={brandName}
                onChange={(event) => onBrandChange(event.target.value)}
                placeholder="Ej.: Clínica Dental Acme"
                inputProps={{ maxLength: 255 }}
                error={Boolean(brandError)}
                helperText={
                  brandError || "Compartido en todos los motores de IA: los cambios se aplican en todos ellos."
                }
                sx={fieldSx}
              />
            </Box>
          )}
        </Box>

        {(countriesQuery.isError || enginesQuery.isError || languagesQuery.isError) && (
          <Typography variant="caption" color="error">
            No se ha podido cargar el catálogo de motores. Cierra y vuelve a abrir el asistente en unos minutos.
          </Typography>
        )}
      </Stack>
    </SetupCard>
  );
}

EngineForm.propTypes = {
  engines: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAdd: PropTypes.func.isRequired,
  defaultCountry: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  onBrandChange: PropTypes.func.isRequired,
  brandError: PropTypes.string,
};

export function SearchEnginesStep({ engines, brand: brandName, keywords, onChange, errors, showErrors, serverErrors }) {
  // Removing an engine drops its keywords too, so nothing orphaned is sent.
  const removeEngine = (key) =>
    onChange((prev) => {
      const nextKeywords = { ...prev.keywords };
      delete nextKeywords[key];
      return { engines: prev.engines.filter((engine) => engine.key !== key), keywords: nextKeywords };
    });

  const classic = engines.filter((engine) => !isAiEngine(engine));
  const ai = engines.filter(isAiEngine);
  const tableErrors = showErrors ? errors : undefined;
  const removeButton = (engine) => (
    <RemoveButton tooltip="Quitar motor" label={`Quitar ${engineTitle(engine)}`} onClick={() => removeEngine(engine.key)} />
  );

  return (
    <Stack spacing={2}>
      <ServerErrors messages={serverErrors} />

      <EngineForm
        engines={engines}
        onAdd={(engine) => onChange((prev) => ({ engines: [...prev.engines, engine] }))}
        defaultCountry={engines[engines.length - 1]?.country}
        brandName={brandName || ""}
        onBrandChange={(value) => onChange({ brand: value })}
        brandError={showErrors ? errors.brand : undefined}
      />

      <SetupCard
        title="Motores de búsqueda añadidos al proyecto"
        aside={
          engines.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
              {engines.length} {engines.length === 1 ? "motor" : "motores"}
            </Typography>
          )
        }
      >
        {engines.length ? (
          <Stack spacing={1.5}>
            {classic.length > 0 && (
              <EngineTable
                engines={classic}
                errors={tableErrors}
                header={{ engine: `Motor de búsqueda (${classic.length})`, trailing: "Keywords" }}
                trailingWidth={96}
                trailing={(engine) => (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums", mr: 0.5 }}>
                      {keywords[engine.key]?.length || 0}
                    </Typography>
                    {removeButton(engine)}
                  </>
                )}
              />
            )}
            {ai.length > 0 && (
              <EngineTable
                engines={ai}
                errors={tableErrors}
                header={{ engine: `Motores de IA (${ai.length})`, trailing: "" }}
                trailingWidth={96}
                trailing={removeButton}
              />
            )}
            {ai.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Los motores de IA siguen la marca «{brandName || "—"}» y no usan keywords.
              </Typography>
            )}
          </Stack>
        ) : (
          <EmptyState
            icon={<Search size={20} />}
            title="Sin motores de búsqueda"
            description="Configura y añade los motores de búsqueda con el formulario de arriba. Puedes incluir varios países, dispositivos y motores de IA."
          />
        )}
        {showErrors && errors.list && (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
            {errors.list}
          </Typography>
        )}
      </SetupCard>
    </Stack>
  );
}

SearchEnginesStep.propTypes = {
  engines: PropTypes.arrayOf(PropTypes.object).isRequired,
  brand: PropTypes.string,
  keywords: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  showErrors: PropTypes.bool,
  serverErrors: PropTypes.arrayOf(PropTypes.string),
};
